import { apiCall } from './apiHelpers';
import { authFetch } from './api';

// Inventory Category API functions
export const inventoryCategoryAPI = {
  // Get all categories for the current account
  getCategories: async () => {
    try {
      return await apiCall(authFetch, '/inventory-categories');
    } catch (error) {
      console.warn('Failed to fetch categories, using default categories:', error);
      // Return default categories as fallback
      return [
        { id: 'electronics', name: 'Electronics' },
        { id: 'clothing', name: 'Clothing' },
        { id: 'books', name: 'Books' },
        { id: 'home', name: 'Home & Garden' },
        { id: 'sports', name: 'Sports' },
        { id: 'automotive', name: 'Automotive' },
        { id: 'health', name: 'Health & Beauty' },
        { id: 'toys', name: 'Toys & Games' },
        { id: 'food', name: 'Food & Beverages' },
        { id: 'other', name: 'Other' }
      ];
    }
  },

  // Get category names only (for dropdowns)
  getCategoryNames: async () => {
    try {
      return await apiCall(authFetch, '/inventory-categories/names');
    } catch (error) {
      console.warn('Failed to fetch category names, using default categories:', error);
      // Return default category names as fallback
      return [
        'Electronics', 'Clothing', 'Books', 'Home & Garden', 'Sports',
        'Automotive', 'Health & Beauty', 'Toys & Games', 'Food & Beverages', 'Other'
      ];
    }
  },

  // Create a new category
  createCategory: async (categoryData) => {
    try {
      return await apiCall(authFetch, '/inventory-categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData)
      });
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  },

  // Update an existing category
  updateCategory: async (id, categoryData) => {
    try {
      return await apiCall(authFetch, `/inventory-categories/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData)
      });
    } catch (error) {
      console.error('Failed to update category:', error);
      throw error;
    }
  },

  // Delete a category
  deleteCategory: async (id) => {
    try {
      return await apiCall(authFetch, `/inventory-categories/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to delete category:', error);
      throw error;
    }
  },

  // Get a specific category by ID
  getCategory: async (id) => {
    try {
      return await apiCall(authFetch, `/inventory-categories/${id}`);
    } catch (error) {
      console.error('Failed to get category:', error);
      throw error;
    }
  }
};


// Enhanced inventory API functions
export const inventoryAPI = {
  // Get all inventory items with filters
  getInventory: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.priceRange?.min) queryParams.append('priceRange.min', filters.priceRange.min);
      if (filters.priceRange?.max) queryParams.append('priceRange.max', filters.priceRange.max);
      if (filters.stockRange?.min !== undefined) queryParams.append('stockRange.min', filters.stockRange.min);
      if (filters.stockRange?.max !== undefined) queryParams.append('stockRange.max', filters.stockRange.max);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const url = `/inventory${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      return await apiCall(authFetch, url);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      throw error;
    }
  },

  // Get inventory statistics
  getStats: async () => {
    try {
      return await apiCall(authFetch, '/inventory/stats');
    } catch (error) {
      console.error('Failed to fetch inventory stats:', error);
      throw error;
    }
  },

  // Create a new inventory item
  createItem: async (itemData) => {
    try {
      return await apiCall(authFetch, '/inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error('Failed to create inventory item:', error);
      throw error;
    }
  },

  // Update an existing inventory item
  updateItem: async (id, itemData) => {
    try {
      return await apiCall(authFetch, `/inventory/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });
    } catch (error) {
      console.error('Failed to update inventory item:', error);
      throw error;
    }
  },

  // Delete an inventory item
  deleteItem: async (id) => {
    try {
      return await apiCall(authFetch, `/inventory/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to delete inventory item:', error);
      throw error;
    }
  },

  // Bulk delete inventory items
  bulkDelete: async (ids) => {
    try {
      return await apiCall(authFetch, '/inventory/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
    } catch (error) {
      console.error('Failed to bulk delete inventory items:', error);
      throw error;
    }
  },

  // Get a specific inventory item by ID
  getItem: async (id) => {
    try {
      return await apiCall(authFetch, `/inventory/${id}`);
    } catch (error) {
      console.error('Failed to get inventory item:', error);
      throw error;
    }
  },

  // Update inventory quantity (deduct stock)
  updateQuantity: async (id, quantity, operation = 'deduct', invoiceId = null, reason = '') => {
    try {
      return await apiCall(authFetch, `/inventory/${id}/quantity`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quantity,
          operation, // 'deduct' or 'add'
          invoiceId, // Track which invoice caused this change
          reason, // Reason for the change
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to update inventory quantity:', error);
      throw error;
    }
  },

  // Bulk update inventory quantities with proper tracking
  bulkUpdateQuantities: async (items, invoiceId = null, operation = 'deduct') => {
    try {
      return await apiCall(authFetch, '/inventory/bulk-update-quantities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(item => ({
            id: item.id,
            quantity: item.quantity,
            operation
          })),
          invoiceId, // Track which invoice caused this change
          timestamp: new Date().toISOString(),
          operation
        })
      });
    } catch (error) {
      console.error('Failed to bulk update inventory quantities:', error);
      throw error;
    }
  },

  // Check if items have sufficient stock
  checkStockAvailability: async (items, invoiceId = null) => {
    try {
      return await apiCall(authFetch, '/inventory/check-stock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(item => ({
            id: item.id,
            quantity: item.quantity
          })),
          invoiceId
        })
      });
    } catch (error) {
      console.error('Failed to check stock availability:', error);
      throw error;
    }
  },

  // Restore stock when invoice is cancelled/deleted
  restoreStockForInvoice: async (invoiceItems, invoiceId, reason = 'Invoice cancelled') => {
    try {
      const inventoryItems = invoiceItems.filter(item => item.inventoryId);

      if (inventoryItems.length === 0) return;

      return await apiCall(authFetch, '/inventory/restore-stock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: inventoryItems.map(item => ({
            id: item.inventoryId,
            quantity: item.quantity || item.qty
          })),
          invoiceId,
          reason,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to restore stock:', error);
      throw error;
    }
  },

  // Get inventory change log for audit
  getInventoryLog: async (productId = null, startDate = null, endDate = null) => {
    try {
      const params = new URLSearchParams();
      if (productId) params.append('productId', productId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      return await apiCall(authFetch, `/inventory/log?${params.toString()}`);
    } catch (error) {
      console.error('Failed to get inventory log:', error);
      throw error;
    }
  },

  // Check if invoice has already processed inventory changes
  checkInventoryProcessed: async (invoiceId) => {
    try {
      return await apiCall(authFetch, `/inventory/check-processed/${invoiceId}`);
    } catch (error) {
      console.error('Failed to check inventory processing status:', error);
      throw error;
    }
  },

  // Mark invoice as inventory processed
  markInventoryProcessed: async (invoiceId) => {
    try {
      return await apiCall(authFetch, `/inventory/mark-processed/${invoiceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processedAt: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to mark invoice as processed:', error);
      throw error;
    }
  }
};
